import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import type { ValueSetCode, ValueSetCodeDesignation } from '../components/records/ValueSetCombobox';

type ValueSetExpansionEntry = {
  system?: string;
  code?: string;
  display?: string;
  designation?: ValueSetCodeDesignation[];
};

export interface ValueSetExpandResponse {
  valueSet: { expansion?: { contains?: ValueSetExpansionEntry[] } };
}

interface UseValueSetExpandParams<TData extends ValueSetExpandResponse, TError, TQueryKey extends readonly unknown[]> {
  queryOptions: (filter: string | undefined) => UseQueryOptions<TData, TError, TData, TQueryKey>;
  enabled?: boolean;
  debounceMs?: number;
}

export interface UseValueSetExpandResult {
  items: ValueSetCode[];
  onQueryChange: (query: string) => void;
  isFetching: boolean;
  error?: string;
}

const toValueSetCodes = (contains: ValueSetExpansionEntry[] | undefined): ValueSetCode[] =>
  (contains ?? []).flatMap((entry) =>
    entry.code
      ? [
          {
            system: entry.system,
            code: entry.code,
            display: entry.display,
            ...(entry.designation && { designation: entry.designation }),
          },
        ]
      : [],
  );

export function useValueSetExpand<
  TData extends ValueSetExpandResponse,
  TError = unknown,
  TQueryKey extends readonly unknown[] = readonly unknown[],
>({
  queryOptions,
  enabled,
  debounceMs = 250,
}: UseValueSetExpandParams<TData, TError, TQueryKey>): UseValueSetExpandResult {
  const [query, setQuery] = useState('');
  const onQueryChange = useDebouncedCallback(setQuery, debounceMs);

  const opts = queryOptions(query || undefined);
  const valueSetQuery = useQuery({ ...opts, ...(enabled !== undefined && { enabled }) });

  const items = useMemo(() => toValueSetCodes(valueSetQuery.data?.valueSet.expansion?.contains), [valueSetQuery.data]);

  return {
    items,
    onQueryChange,
    isFetching: valueSetQuery.isFetching,
    error: valueSetQuery.error instanceof Error ? valueSetQuery.error.message : undefined,
  };
}
